import { HttpErrorResponse } from '@angular/common/http';

// Rótulos amigáveis para os campos que o back devolve em erros de validação.
const FIELD_LABELS: { [key: string]: string } = {
  name: 'Nome',
  cpf_or_cnpj: 'CPF/CNPJ',
  cpf_or_cnpj_property: 'CPF/CNPJ do proprietário',
  rg: 'RG',
  birth_date: 'Data de nascimento',
  zipcode: 'CEP',
  address: 'Endereço',
  neighborhood: 'Bairro',
  city: 'Cidade',
  cellphone: 'Telefone celular',
  email: 'Email',
  password: 'Senha',
  store_name: 'Referência comercial',
  owner_property: 'Fazenda',
  owner_name: 'Titular da propriedade',
  owner_zipcode: 'CEP da propriedade',
  owner_address: 'Endereço da propriedade',
  owner_state: 'UF da propriedade',
  owner_city: 'Município da propriedade',
  ie_incra: 'Inscrição Estadual/INCRA',
  stablisment_code: 'Cód. Estabelecimento/IMA',
  terms_accepted: 'Termos e condições',
};

// Parte das validações do DRF não está traduzida no back e chegaria em inglês
// para o usuário. Traduzimos as mais comuns; o que não estiver aqui passa direto.
const DRF_MESSAGES: { [key: string]: string } = {
  'this field is required.': 'é obrigatório.',
  'this field may not be blank.': 'não pode ficar em branco.',
  'this field may not be null.': 'é obrigatório.',
  'enter a valid email address.': 'informe um email válido.',
  'a valid integer is required.': 'informe um número válido.',
  'a valid number is required.': 'informe um número válido.',
  'not a valid string.': 'valor inválido.',
  'invalid token.': 'sessão expirada. Faça login novamente.',
  'user not found': 'não encontramos nenhum cadastro com esse email.',
};

// Mensagens com número dentro não cabem no mapa acima.
const DRF_PATTERNS: Array<[RegExp, (match: RegExpMatchArray) => string]> = [
  [
    /^ensure this field has no more than (\d+) characters\.$/,
    (m) => `não pode passar de ${m[1]} caracteres.`,
  ],
  [
    /^ensure this field has at least (\d+) characters\.$/,
    (m) => `precisa ter pelo menos ${m[1]} caracteres.`,
  ],
  [/^date has wrong format\./, () => 'está em um formato de data inválido.'],
];

function translate(message: string): string {
  const key = message.trim().toLowerCase();

  if (DRF_MESSAGES[key]) return DRF_MESSAGES[key];

  for (const [pattern, build] of DRF_PATTERNS) {
    const match = key.match(pattern);
    if (match) return build(match);
  }

  return message;
}

// Usado quando o corpo da resposta não traz nada legível (página HTML de erro
// do Django, gateway fora do ar, requisição bloqueada antes de sair).
function messageByStatus(status: number): string {
  if (status === 0) {
    return 'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.';
  }
  if (status === 400) {
    return 'Alguns dados estão inválidos. Revise os campos preenchidos e tente novamente.';
  }
  if (status === 401 || status === 403) {
    return 'Você não tem permissão para concluir esta operação.';
  }
  if (status === 404) {
    return 'Recurso não encontrado no servidor.';
  }
  if (status === 408 || status === 504) {
    return 'O servidor demorou demais para responder. Tente novamente em alguns minutos.';
  }
  if (status >= 500) {
    return 'O servidor falhou ao processar a solicitação. Tente novamente em alguns minutos.';
  }
  return 'Não foi possível concluir a operação. Tente novamente.';
}

// Chaves que só embrulham a mensagem: prefixar o texto com elas não ajuda
// ninguém ("errors: Email: ..."), então o conteúdo sobe sem o rótulo.
const WRAPPER_KEYS = [
  'detail',
  'details',
  'error',
  'errors',
  'erro',
  'erros',
  'message',
  'messages',
  'mensagem',
  'msg',
  'non_field_errors',
];

// Chaves que carregam dado de máquina, não texto para o usuário.
const NOISE_KEYS = ['code', 'status', 'status_code', 'traceback', 'stack', 'stacktrace'];

// O toast não comporta um corpo de resposta inteiro.
const MAX_LENGTH = 300;

function labelFor(field: string): string {
  return FIELD_LABELS[field] || field;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function withLabel(field: string, text: string): string {
  // Índice de lista que virou chave de objeto ("0", "1"): não é nome de campo.
  const isIndex = /^\d+$/.test(field);
  const label = labelFor(field);

  // O back já costuma citar o campo na própria frase ("O RG é obrigatório.");
  // repetir o rótulo nesse caso só deixaria a mensagem redundante.
  if (isIndex || WRAPPER_KEYS.includes(field) || text.toLowerCase().includes(label.toLowerCase())) {
    return capitalize(text);
  }

  return `${label}: ${text}`;
}

/**
 * Recolhe todo texto legível do corpo da resposta, por mais aninhado que venha.
 *
 * O back não tem um formato único de erro: às vezes é `{ detail: "..." }`, às
 * vezes `{ campo: ["..."] }`, às vezes uma lista ou um objeto dentro de outro.
 * Em vez de reconhecer cada formato, descemos no valor inteiro — o que o back
 * mandou é sempre mais útil que um texto genérico nosso.
 */
function collectMessages(value: any): string[] {
  if (value === null || value === undefined) return [];

  if (typeof value === 'string') {
    const text = value.trim();
    return text ? [translate(text)] : [];
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectMessages);
  }

  if (typeof value === 'object') {
    return Object.keys(value)
      .filter((key) => !NOISE_KEYS.includes(key))
      .flatMap((key) => {
        const text = collectMessages(value[key]).join(' ');
        return text ? [withLabel(key, text)] : [];
      });
  }

  return [];
}

function decodeEntities(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Página de erro HTML do Django. Mesmo aqui há mensagem aproveitável: com
 * DEBUG ligado a exceção fica no `exception_value`, e sem DEBUG sobra o título.
 */
function messageFromHtml(html: string): string | null {
  const exception = html.match(/<pre class="exception_value">([\s\S]*?)<\/pre>/i);
  const title = html.match(/<title>([\s\S]*?)<\/title>/i);
  const raw = exception?.[1] ?? title?.[1];

  if (!raw) return null;

  const text = decodeEntities(raw.replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
  return text || null;
}

function truncate(text: string): string {
  return text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH - 1).trimEnd()}…` : text;
}

/**
 * Traduz um erro HTTP na mensagem que será exibida ao usuário.
 *
 * O que o back mandou tem prioridade sobre qualquer texto nosso, em qualquer
 * formato que venha — inclusive a página HTML de erro do Django. O texto por
 * status só entra quando a resposta realmente não trouxe nada legível, que é
 * o caso de rede fora do ar, CORS barrando a resposta ou corpo vazio.
 *
 * As mensagens do back saem literais (só traduzidas quando vêm em inglês), pois
 * há telas que comparam esse texto: login, lance e esqueci a senha.
 */
export function extractHttpErrorMessage(error: HttpErrorResponse): string {
  // Falha do lado do browser (rede caiu, CORS, requisição abortada): não existe
  // corpo de resposta para aproveitar.
  if (error.error instanceof ErrorEvent || error.error instanceof ProgressEvent) {
    return messageByStatus(error.status);
  }

  const body = error.error;

  if (typeof body === 'string') {
    const text = body.trim();
    if (!text) return messageByStatus(error.status);

    const isHtml = text.startsWith('<') || text.toLowerCase().includes('<!doctype');
    if (!isHtml) return truncate(capitalize(translate(text)));

    const fromHtml = messageFromHtml(text);
    return fromHtml ? truncate(fromHtml) : messageByStatus(error.status);
  }

  if (body && typeof body === 'object') {
    const messages = collectMessages(body);
    if (messages.length) return truncate(messages.join('; '));
  }

  return messageByStatus(error.status);
}
